import { DestroyRef, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { getState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, of, pipe, switchMap } from 'rxjs';
import {
  ApiGuardianConfigurationDetails,
  ApiGuardianEndpointDetails,
  APIGuardianService,
  GetApiGuardianEndpointMetricsResponse,
  GetApiGuardianMetricsResponse,
  GetApiGuardianResponse,
  MetricsService,
  ShieldApiStore,
  UpdateApiGuardianConfigurationRequest,
  UpdateApiGuardianEndpointRequest,
  UpsertOpenApiSpecificationRequest,
} from '@bn/app-shield';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState } from '@bn/shared/state-store';
import { BnToastrService } from '@bn/web-ui/toastr';
import { MapShieldApiGuardianChartData, ShieldApiGuardianChartData } from './helpers/shield-api-store.helper';

const events = getStoreEvents('SHIELD_API_GUARDIAN', ['GET', 'UPDATE', 'UPLOAD', 'UPDATE_ENDPOINT', 'UPDATE_CONFIG', 'METRICS', 'ENDPOINT_METRICS', 'ENUMS']);
const UPLOAD_SPEC_ERROR = 'Failed to upload OpenAPI specification.';
const UPDATE_SPEC_ERROR = 'Failed to update OpenAPI specification.';

export type ApiGuardianEnums = { [key: string]: { [key: string]: string } };

export interface ApiGuardianState {
  shieldZoneId: number | undefined;
  configuration: ApiGuardianConfigurationDetails | undefined;
  endpoints: ApiGuardianEndpointDetails[];
  chartMetrics: ShieldApiGuardianChartData | undefined;
  endpointChartMetrics: ShieldApiGuardianChartData | undefined;
  enums: ApiGuardianEnums | undefined;
  isLoaded: boolean;
}

const initialState: ApiGuardianState = {
  shieldZoneId: undefined,
  configuration: undefined,
  endpoints: [],
  chartMetrics: undefined,
  endpointChartMetrics: undefined,
  enums: undefined,
  isLoaded: false,
};

export const ShieldApiGuardianStore = signalStore(
  { providedIn: 'root' },
  withDevtools('Shield.ApiGuardian'),
  withState<ApiGuardianState>(initialState),
  withDefaultRequestStatusState(),

  withMethods((store, apiGuardianService = inject(APIGuardianService), destroyRef = inject(DestroyRef)) => ({
    getApiGuardian: rxMethod<{ shieldZoneId: number; cleanRequest?: boolean }>(
      exhaustMap(({ shieldZoneId, cleanRequest }) => {
        const currentShieldZoneId = store.shieldZoneId ? store.shieldZoneId() : null;

        if (currentShieldZoneId === shieldZoneId && store.isLoaded() && !cleanRequest) {
          return of(null);
        }

        updateState(store, events.GET.REQUEST, { endpoints: [], configuration: undefined, shieldZoneId }, store.setLoading(StoreRequest.Item));

        return apiGuardianService.getAPIGuardian({ shieldZoneId }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response: GetApiGuardianResponse) => {
              const { error, data } = response;

              if (error && !error?.success) {
                updateState(
                  store,
                  events.GET.ERROR,
                  { isLoaded: true },
                  store.setErrorMessage(StoreRequest.Item, error?.message ?? 'There was an error while trying to get API Guardian data.'),
                );
                return;
              }

              updateState(
                store,
                events.GET.SUCCESS,
                {
                  shieldZoneId,
                  configuration: data?.configuration,
                  endpoints: data?.endpoints ?? [],
                  isLoaded: true,
                },
                store.setFinished(StoreRequest.Item),
              );
            },
            error: ({ error }) => updateState(store, events.GET.ERROR, { isLoaded: true }, store.setError(StoreRequest.Item, error)),
          }),
        );
      }),
    ),

    clearState: (): void => {
      updateState(store, events.GET.CLEAR, initialState, store.setDefault([StoreRequest.Item]));
    },

    getEnums: rxMethod<{ shieldZoneId: number }>(
      pipe(
        switchMap(({ shieldZoneId }) => {
          return apiGuardianService.getAPIGuardianEnums({ shieldZoneId: String(shieldZoneId) }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (data) => {
                updateState(store, events.ENUMS.SUCCESS, { enums: data });
              },
              error: () => {
                // Enums are optional, silently ignore errors
              },
            }),
          );
        }),
      ),
    ),
  })),

  withMethods((store, metricsService = inject(MetricsService), destroyRef = inject(DestroyRef)) => ({
    getMetrics: rxMethod<{ shieldZoneId: number }>(
      pipe(
        switchMap(({ shieldZoneId }) => {
          updateState(store, events.METRICS.REQUEST, store.setLoading(StoreRequest.Item, ['chartMetrics']));

          return metricsService.getShieldZoneAPIGuardianMetrics({ shieldZoneId }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response: GetApiGuardianMetricsResponse) => {
                const { data } = response;

                updateState(
                  store,
                  events.METRICS.SUCCESS,
                  { chartMetrics: MapShieldApiGuardianChartData(data) },
                  store.setFinished(StoreRequest.Item, ['chartMetrics']),
                );
              },
              error: () =>
                updateState(
                  store,
                  events.METRICS.SUCCESS,
                  { chartMetrics: MapShieldApiGuardianChartData(undefined) },
                  store.setFinished(StoreRequest.Item, ['chartMetrics']),
                ),
            }),
          );
        }),
      ),
    ),

    getEndpointMetrics: rxMethod<{ shieldZoneId: number; endpointId: number }>(
      pipe(
        switchMap(({ shieldZoneId, endpointId }) => {
          updateState(
            store,
            events.ENDPOINT_METRICS.REQUEST,
            { endpointChartMetrics: undefined },
            store.setLoading(StoreRequest.Item, ['endpointChartMetrics']),
          );

          return metricsService.getAPIGuardianEndpointMetrics({ shieldZoneId, endpointId }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response: GetApiGuardianEndpointMetricsResponse) => {
                const { data } = response;

                updateState(
                  store,
                  events.ENDPOINT_METRICS.SUCCESS,
                  { endpointChartMetrics: MapShieldApiGuardianChartData(data) },
                  store.setFinished(StoreRequest.Item, ['endpointChartMetrics']),
                );
              },
              error: () =>
                updateState(
                  store,
                  events.ENDPOINT_METRICS.SUCCESS,
                  { endpointChartMetrics: MapShieldApiGuardianChartData(undefined) },
                  store.setFinished(StoreRequest.Item, ['endpointChartMetrics']),
                ),
            }),
          );
        }),
      ),
    ),
  })),

  withMethods((store, apiGuardianService = inject(APIGuardianService), toastrService = inject(BnToastrService), destroyRef = inject(DestroyRef)) => ({
    uploadOpenApiSpec: rxMethod<{ shieldZoneId: number; data: UpsertOpenApiSpecificationRequest }>(
      switchMap(({ shieldZoneId, data }) => {
        updateState(store, events.UPLOAD.REQUEST, store.setLoading(StoreRequest.Add));

        return apiGuardianService.uploadOpenAPISpecification({ shieldZoneId, upsertOpenApiSpecificationRequest: data }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data: responseData } = response;

              if (error && !error?.success) {
                toastrService.danger(error?.message ?? UPLOAD_SPEC_ERROR);
                updateState(store, events.UPLOAD.ERROR, store.setErrorMessage(StoreRequest.Add, error?.message ?? UPLOAD_SPEC_ERROR));
                return;
              }

              toastrService.success('OpenAPI specification uploaded successfully.');

              updateState(
                store,
                events.UPLOAD.SUCCESS,
                {
                  configuration: responseData?.configuration,
                  endpoints: responseData?.endpoints ?? [],
                  isLoaded: true,
                },
                store.setFinished(StoreRequest.Add),
              );
            },
            error: ({ error }) => {
              toastrService.danger(error?.message ?? UPLOAD_SPEC_ERROR);
              updateState(store, events.UPLOAD.ERROR, store.setError(StoreRequest.Add, error));
            },
          }),
        );
      }),
    ),
  })),

  withMethods((store, apiGuardianService = inject(APIGuardianService), toastrService = inject(BnToastrService), destroyRef = inject(DestroyRef)) => ({
    updateOpenApiSpec: rxMethod<{ shieldZoneId: number; data: UpsertOpenApiSpecificationRequest }>(
      switchMap(({ shieldZoneId, data }) => {
        updateState(store, events.UPDATE.REQUEST, store.setLoading(StoreRequest.Update, ['openApiSpec']));

        return apiGuardianService.updateOpenAPISpecification({ shieldZoneId, upsertOpenApiSpecificationRequest: data }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data: responseData } = response;

              if (error && !error?.success) {
                toastrService.danger(error?.message ?? UPDATE_SPEC_ERROR);
                updateState(store, events.UPDATE.ERROR, store.setErrorMessage(StoreRequest.Update, error?.message ?? UPDATE_SPEC_ERROR, ['openApiSpec']));
                return;
              }

              toastrService.success('API Guardian configuration updated successfully.');

              updateState(
                store,
                events.UPDATE.SUCCESS,
                {
                  configuration: responseData?.configuration,
                  endpoints: responseData?.endpoints ?? [],
                },
                store.setFinished(StoreRequest.Update, ['openApiSpec']),
              );
            },
            error: ({ error }) => {
              toastrService.danger(error?.message ?? UPDATE_SPEC_ERROR);
              updateState(store, events.UPDATE.ERROR, store.setError(StoreRequest.Update, error, ['openApiSpec']));
            },
          }),
        );
      }),
    ),
  })),

  withMethods((store, apiGuardianService = inject(APIGuardianService), toastrService = inject(BnToastrService), destroyRef = inject(DestroyRef)) => ({
    updateConfiguration: rxMethod<{ shieldZoneId: number; data: UpdateApiGuardianConfigurationRequest; customFields?: string[] }>(
      switchMap(({ shieldZoneId, data, customFields }) => {
        updateState(store, events.UPDATE_CONFIG.REQUEST, store.setLoading(StoreRequest.Update, customFields));

        return apiGuardianService.updateAPIGuardianConfiguration({ shieldZoneId, updateApiGuardianConfigurationRequest: data }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data: updatedConfig } = response;

              if (error && !error?.success) {
                updateState(
                  store,
                  events.UPDATE_CONFIG.ERROR,
                  store.setErrorMessage(StoreRequest.Update, error?.message ?? 'There was an error while updating the configuration.', customFields),
                );
                return;
              }

              toastrService.success('Configuration updated successfully.');

              updateState(
                store,
                events.UPDATE_CONFIG.SUCCESS,
                {
                  configuration: updatedConfig ?? store.configuration(),
                },
                store.setFinished(StoreRequest.Update, customFields),
              );
            },
            error: ({ error }) => updateState(store, events.UPDATE_CONFIG.ERROR, store.setError(StoreRequest.Update, error, customFields)),
          }),
        );
      }),
    ),
  })),

  withMethods((store, apiGuardianService = inject(APIGuardianService), toastrService = inject(BnToastrService), destroyRef = inject(DestroyRef)) => ({
    updateEndpoint: rxMethod<{ shieldZoneId: number; endpointId: number; data: UpdateApiGuardianEndpointRequest; customFields?: string[] }>(
      switchMap(({ shieldZoneId, endpointId, data, customFields }) => {
        updateState(store, events.UPDATE_ENDPOINT.REQUEST, store.setLoading(StoreRequest.Update, customFields));

        return apiGuardianService
          .updateAPIGuardianEndpoint({
            shieldZoneId,
            endpointId,
            updateApiGuardianEndpointRequest: data,
          })
          .pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response) => {
                const { error, data: updatedEndpoint } = response;

                if (error && !error?.success) {
                  updateState(
                    store,
                    events.UPDATE_ENDPOINT.ERROR,
                    store.setErrorMessage(StoreRequest.Update, error?.message ?? 'There was an error while updating the endpoint.', customFields),
                  );
                  return;
                }

                toastrService.success('Endpoint updated successfully.');

                const currentEndpoints = getState(store).endpoints;
                const updatedEndpoints = currentEndpoints.map((ep) => (ep.apiGuardianEndpointId === endpointId ? { ...ep, ...updatedEndpoint } : ep));

                updateState(
                  store,
                  events.UPDATE_ENDPOINT.SUCCESS,
                  {
                    endpoints: updatedEndpoints,
                  },
                  store.setFinished(StoreRequest.Update, customFields),
                );
              },
              error: ({ error }) => updateState(store, events.UPDATE_ENDPOINT.ERROR, store.setError(StoreRequest.Update, error, customFields)),
            }),
          );
      }),
    ),
  })),

  withHooks({
    onInit(store) {
      const shieldStore = inject(ShieldApiStore);

      effect(() => {
        const id = Number(shieldStore.selectedEntityId()()) || null;

        untracked(() => {
          if (id) {
            store.getApiGuardian({
              shieldZoneId: id,
              cleanRequest: true,
            });
            store.getMetrics({ shieldZoneId: id });
            store.getEnums({ shieldZoneId: id });
          }
        });
      });
    },
  }),
);
